/**
 * OnboardingAccountPage — iter119
 *
 * Step 1 of the restaurant onboarding wizard. A single page that creates
 * the user + restaurant accounts so the owner has a tangible result before
 * being asked to pick a plan or connect Stripe.
 *
 * Fields:
 *   - Restaurant Name (required)
 *   - Owner Name (optional)
 *   - Email (required, must have TLD per UserRegister.field_validator)
 *   - Password (required, min 8)
 *   - Confirm Password (required, must match)
 *   - Phone (required)
 *   - Website (optional)
 *   - Address (street, required) + ZIP (required) + City (autofilled from ZIP)
 *     + State (autofilled from ZIP, can override)
 *
 * Behavior:
 *   - ZIP-based City/State autofill via `useZipLookup` so owners don't fight
 *     with the address fields.
 *   - The form ALWAYS renders empty, even for already-authenticated visitors
 *     who click "Step 1" in the stepper to review or create another account.
 *     Per the product owner: clicking Step 1 means "show me the signup form
 *     from scratch — do NOT bounce me away to the dashboard or my last step".
 *   - iter119 — Local DRAFT auto-save (localStorage). As the owner types, we
 *     persist the non-sensitive form values to localStorage under
 *     `nofee_onboarding_account_draft`. If they navigate away (e.g. to Step 2)
 *     and come back, the form rehydrates from the draft. Passwords are NEVER
 *     written to localStorage. The draft is cleared on successful submit.
 *   - iter119 — Strict address validation BEFORE creating the account. We
 *     call `POST /api/public/validate-address` to enforce: (a) ZIP is real,
 *     (b) ZIP↔State consistency (e.g. ZIP 33101 with state MA → REJECTED),
 *     (c) Nominatim can geocode the full street/city/state/ZIP to a real
 *     location. If any check fails, submit is blocked with a clear inline
 *     error and an actionable suggestion (e.g. "ZIP 33101 belongs to FL").
 *   - On success: stores the JWT via the AuthContext login flow and
 *     navigates to /onboarding/plan (Step 2).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Store, User, Mail, Lock, Eye, EyeOff, Phone, Globe, MapPin, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import OnboardingShell from '../components/OnboardingShell';
import { useAuth } from '../context/AuthContext';
import { US_STATES } from '../utils/usStates';
import AgreementCheckbox from '../components/AgreementCheckbox';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  restaurant_name: '',
  owner_name: '',
  email: '',
  password: '',
  confirm_password: '',
  phone: '',
  website: '',
  address: '',
  zip_code: '',
  city: '',
  state: '',
};

// iter119 — localStorage key for the Step 1 draft. NEVER stores passwords.
const DRAFT_KEY = 'nofee_onboarding_account_draft';
const DRAFT_FIELDS = [
  'restaurant_name', 'owner_name', 'email', 'phone', 'website',
  'address', 'zip_code', 'city', 'state',
];

function loadDraft() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(DRAFT_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // Keep only known fields so legacy payloads can't inject anything.
    const filtered = {};
    DRAFT_FIELDS.forEach((k) => {
      if (typeof parsed[k] === 'string') filtered[k] = parsed[k];
    });
    return filtered;
  } catch {
    return null;
  }
}

function saveDraft(form) {
  try {
    if (typeof window === 'undefined') return;
    const snapshot = {};
    DRAFT_FIELDS.forEach((k) => { snapshot[k] = form[k] || ''; });
    // Skip saving when the form is empty — keeps localStorage clean for
    // brand-new visitors who never typed anything.
    const hasAny = Object.values(snapshot).some((v) => v && v.length > 0);
    if (!hasAny) {
      window.localStorage.removeItem(DRAFT_KEY);
      return;
    }
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot));
  } catch {
    /* localStorage unavailable (private mode, full disk) — silently skip. */
  }
}

function clearDraft() {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(DRAFT_KEY);
  } catch { /* noop */ }
}

export default function OnboardingAccountPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();

  // iter119 — Rehydrate from localStorage draft (without passwords) so the
  // owner doesn't lose their typing when they bounce between steps.
  const [form, setForm] = useState(() => {
    const draft = loadDraft();
    return draft ? { ...EMPTY, ...draft } : EMPTY;
  });
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // iter125 — Restaurant Agreement consent. Must be ticked before submit;
  // pinned to bottom of the form so it's the last action the owner takes,
  // right next to the Create Account CTA. The link opens the agreement in
  // a new tab so the form draft is preserved.
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);
  // iter119 — sticky banner shown when `/api/public/validate-address` fails
  // (ZIP↔State mismatch, fake street, etc.). Cleared as soon as the owner
  // edits any address field so it never blocks them after they've fixed it.
  const [addrError, setAddrError] = useState('');

  // iter119 — debounce + persist draft to localStorage whenever the form
  // changes. Passwords are NEVER included (DRAFT_FIELDS is the allowlist).
  useEffect(() => {
    const t = setTimeout(() => saveDraft(form), 300);
    return () => clearTimeout(t);
  }, [form]);

  // Carry-over codes from referral / partner links.
  const partnerCode = params.get('partner') || '';
  const planParam = params.get('plan') || 'ordering';

  // NOTE (iter118): We intentionally do NOT redirect authenticated users
  // away from this page. Step 1 is reachable from the wizard stepper at any
  // time, and the form must always render empty so owners can create another
  // account or simply review the Step 1 UI. The previous auto-redirect to
  // /onboarding was bouncing logged-in owners to their last incomplete step,
  // which broke "click Step 1 in the progress bar" as a navigation gesture.

  // ZIP → City / State autofill (iter116). We call the PUBLIC zip-lookup
  // endpoint because Step 1 is the pre-auth signup page; the authenticated
  // /api/address/zip-lookup would 401. Result also feeds the inline ZIP
  // mismatch warning via local state below.
  const [zipWarn, setZipWarn] = useState('');
  const [zipLoading, setZipLoading] = useState(false);
  useEffect(() => {
    const zip = (form.zip_code || '').replace(/\D/g, '').slice(0, 5);
    if (zip.length !== 5) { setZipWarn(''); return; }
    let cancelled = false;
    setZipLoading(true);
    axios
      .get(`${API}/api/public/zip-lookup/${zip}`)
      .then((r) => {
        if (cancelled) return;
        const d = r?.data || {};
        if (!d.valid) { setZipWarn('Please enter a valid U.S. ZIP Code.'); return; }
        const expected = (d.state || '').toUpperCase();
        setForm((f) => {
          const providedState = (f.state || '').toUpperCase();
          if (providedState && expected && providedState !== expected) {
            setZipWarn(`ZIP ${zip} belongs to ${expected}, not ${providedState}. Please correct the State before saving.`);
          } else {
            setZipWarn('');
          }
          return {
            ...f,
            city:  f.city  || d.city  || '',
            state: f.state || expected || '',
          };
        });
      })
      .catch(() => { /* soft-fail — server-side validator catches bad ZIPs at save */ })
      .finally(() => { if (!cancelled) setZipLoading(false); });
    return () => { cancelled = true; };
  }, [form.zip_code]);

  const setField = (key) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
    setErrors((er) => ({ ...er, [key]: undefined }));
    // iter119 — clear the sticky address banner the moment the owner
    // edits any address-related field so they're not stuck staring at a
    // red banner after they've already fixed the mismatch.
    if (key === 'address' || key === 'zip_code' || key === 'city' || key === 'state') {
      setAddrError('');
    }
  };

  const emailValid = useMemo(
    () => /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(form.email.trim()),
    [form.email],
  );

  const validate = () => {
    const e = {};
    if (!form.restaurant_name.trim()) e.restaurant_name = 'Required.';
    if (!form.email.trim() || !emailValid) e.email = 'Enter a valid email (e.g. you@restaurant.com).';
    if (!form.password || form.password.length < 8) e.password = 'At least 8 characters.';
    if (form.password !== form.confirm_password) e.confirm_password = 'Passwords do not match.';
    if (!form.phone.trim()) e.phone = 'Required.';
    if (!form.address.trim()) e.address = 'Required.';
    if (!/^\d{5}$/.test((form.zip_code || '').trim())) e.zip_code = '5-digit ZIP required.';
    if (!form.city.trim()) e.city = 'Required.';
    if (!form.state.trim()) e.state = 'Required.';
    if (form.website && form.website.trim() && !/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}.*$/i.test(form.website.trim())) {
      e.website = 'Please enter a valid URL (e.g. yourrestaurant.com).';
    }
    // iter125 — Restaurant Agreement must be accepted before account creation.
    if (!acceptedAgreement) {
      e.acceptedAgreement = 'Please review and accept the Restaurant Agreement to continue.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setAddrError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      // iter119 — STRICT address validation BEFORE creating the account.
      // Catches Boston-street + Florida-ZIP combos that the owner explicitly
      // flagged as unacceptable. Backend already cross-validates at register
      // time, but blocking client-side too means the owner sees the exact
      // error inline instead of getting a generic 400 after submit.
      let resolvedCity = form.city.trim();
      let resolvedState = form.state.trim().toUpperCase();
      try {
        const vr = await axios.post(`${API}/api/public/validate-address`, {
          street: form.address.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          zip_code: form.zip_code.trim(),
        });
        const v = vr?.data || {};
        if (!v.valid) {
          const msg = v.error_message || 'Please double-check the street, city, state and ZIP — they do not match a real U.S. address.';
          setAddrError(msg);
          toast.error(msg);
          setSubmitting(false);
          return;
        }
        // Trust the backend's canonical resolution so a typo in city ("Bosotn"
        // → "Boston") doesn't bounce back as a Pydantic error after register.
        if (v.resolved_city)  resolvedCity  = v.resolved_city;
        if (v.resolved_state) resolvedState = v.resolved_state;
      } catch (vErr) {
        // Network blip on the validator → degrade gracefully and let the
        // register endpoint do server-side validation. We don't block the
        // user just because the geocoder is temporarily unavailable.
        // (No-op intentionally.)
        void vErr;
      }

      const payload = {
        name: form.owner_name.trim() || form.restaurant_name.trim(),
        owner_name: form.owner_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim(),
        restaurant_name: form.restaurant_name.trim(),
        address: form.address.trim(),
        zip_code: form.zip_code.trim(),
        city: resolvedCity,
        state: resolvedState,
        website: form.website.trim(),
        partner_code: partnerCode || undefined,
        plan: planParam || undefined,
      };
      const res = await axios.post(`${API}/api/auth/register`, payload);
      // Store JWT via AuthContext so downstream pages see the auth token.
      // The login() helper in AuthContext expects (email, password) — we
      // already created the user, so call login with the same creds to
      // reuse its token-persistence side-effects.
      try {
        await login(payload.email, payload.password);
      } catch {
        // If auto-login fails, persist token from the register response
        // directly so the user still moves forward.
        if (res?.data?.token) {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('nofee_token', res.data.token);
        }
      }
      // iter119 — Account created successfully → drop the draft. From here
      // on the user lives in /onboarding/plan; coming back to Step 1 should
      // show an empty form (per iter118 product spec).
      clearDraft();
      toast.success('Account created — let\'s pick your plan.');
      navigate('/onboarding/plan');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = 'Could not create your account. Please try again.';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = String(detail[0].msg).replace(/^Value error,\s*/i, '');
      }
      // iter119 — surface ZIP/State/address related backend errors in the
      // sticky address banner too so the owner can see what to fix without
      // hunting through field-level red text.
      if (/zip|state|address|city/i.test(msg)) {
        setAddrError(msg);
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingShell
      currentStep="account_created"
      backFallback="/"
      testid="onboarding-account-page"
      hideAuthChrome
    >
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
          <Store className="w-3.5 h-3.5 text-yellow-300" />
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
            Step 1 · Create your account
          </span>
        </div>
        <h1
          data-testid="account-headline"
          className="mt-5 font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white"
        >
          Let&rsquo;s get your restaurant{' '}
          <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
            on NoFee
          </span>
          .
        </h1>
        <p className="mt-4 text-[15px] sm:text-base text-white/65 leading-relaxed max-w-xl">
          Tell us a bit about your restaurant. You can stop anytime — your progress is saved and
          you&rsquo;ll resume right where you left off the next time you sign in.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        data-testid="account-form"
        className="mt-9 max-w-2xl rounded-3xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-7 space-y-5"
        noValidate
      >
        {/* Restaurant Name (required) */}
        <Field
          label="Restaurant Name"
          required
          Icon={Store}
          error={errors.restaurant_name}
        >
          <input
            type="text"
            value={form.restaurant_name}
            onChange={setField('restaurant_name')}
            placeholder="e.g. La Bottega"
            maxLength={160}
            data-testid="account-input-restaurant-name"
            className={inputCls}
          />
        </Field>

        {/* Owner Name (optional) */}
        <Field
          label="Owner Name"
          optional
          Icon={User}
          error={errors.owner_name}
        >
          <input
            type="text"
            value={form.owner_name}
            onChange={setField('owner_name')}
            placeholder="e.g. Mario Rossi"
            maxLength={120}
            data-testid="account-input-owner-name"
            className={inputCls}
          />
        </Field>

        {/* Email + Phone — side by side on desktop */}
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Email" required Icon={Mail} error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={setField('email')}
              placeholder="you@yourrestaurant.com"
              maxLength={160}
              data-testid="account-input-email"
              className={inputCls}
              autoComplete="email"
            />
          </Field>
          <Field label="Phone" required Icon={Phone} error={errors.phone}>
            <input
              type="tel"
              value={form.phone}
              onChange={setField('phone')}
              placeholder="(555) 555-5555"
              maxLength={32}
              data-testid="account-input-phone"
              className={inputCls}
              autoComplete="tel"
            />
          </Field>
        </div>

        {/* Password + Confirm — side by side */}
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Password" required Icon={Lock} error={errors.password}>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={form.password}
                onChange={setField('password')}
                placeholder="At least 8 characters"
                maxLength={128}
                data-testid="account-input-password"
                className={inputCls + ' pr-10'}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
          <Field label="Confirm Password" required Icon={Lock} error={errors.confirm_password}>
            <div className="relative">
              <input
                type={showCpw ? 'text' : 'password'}
                value={form.confirm_password}
                onChange={setField('confirm_password')}
                placeholder="Re-enter your password"
                maxLength={128}
                data-testid="account-input-confirm-password"
                className={inputCls + ' pr-10'}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowCpw((s) => !s)}
                aria-label={showCpw ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
              >
                {showCpw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
        </div>

        {/* Website (optional) */}
        <Field label="Website" optional Icon={Globe} error={errors.website}>
          <input
            type="url"
            value={form.website}
            onChange={setField('website')}
            placeholder="https://yourrestaurant.com"
            maxLength={250}
            data-testid="account-input-website"
            className={inputCls}
            autoComplete="url"
          />
        </Field>

        {/* Address */}
        <Field label="Street Address" required Icon={MapPin} error={errors.address}>
          <input
            type="text"
            value={form.address}
            onChange={setField('address')}
            placeholder="100 Atlantic Ave"
            maxLength={250}
            data-testid="account-input-address"
            className={inputCls}
            autoComplete="address-line1"
          />
          <p className="mt-1 text-[11px] text-white/40">
            Street and number only — city, state and ZIP go below.
          </p>
        </Field>

        {/* ZIP + City + State */}
        <div className="grid sm:grid-cols-[140px_1fr_180px] gap-5">
          <Field label="ZIP Code" required error={errors.zip_code}>
            <input
              type="text"
              value={form.zip_code}
              onChange={(e) => setField('zip_code')({ target: { value: e.target.value.replace(/\D/g, '').slice(0, 5) } })}
              placeholder="02110"
              data-testid="account-input-zip"
              className={inputCls}
              inputMode="numeric"
              maxLength={5}
              autoComplete="postal-code"
            />
            {zipLoading && (
              <p className="mt-1 text-[11px] text-white/40">Looking up city…</p>
            )}
          </Field>
          <Field label="City" required error={errors.city}>
            <input
              type="text"
              value={form.city}
              onChange={setField('city')}
              placeholder="Boston"
              data-testid="account-input-city"
              className={inputCls}
              autoComplete="address-level2"
            />
          </Field>
          <Field label="State" required error={errors.state}>
            <select
              value={form.state}
              onChange={setField('state')}
              data-testid="account-input-state"
              className={inputCls + ' appearance-none'}
            >
              <option value="" className="bg-zinc-900">— Select —</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code} className="bg-zinc-900">
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {zipWarn && (
          <p className="text-[12px] text-amber-300 font-semibold" data-testid="account-zip-warn">{zipWarn}</p>
        )}

        {/* iter119 — sticky address-validation error banner. Shown when
            `/api/public/validate-address` rejects the combo (e.g. Boston
            street + Florida ZIP) or when register itself returns a
            ZIP/State/City-related backend error. Auto-clears the moment
            the owner edits any address field. */}
        {addrError && (
          <div
            data-testid="account-address-error"
            className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl border border-rose-500/40 bg-rose-500/[0.10]"
          >
            <AlertTriangle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-rose-100 leading-snug">
              <span className="font-bold text-rose-200">Address can&rsquo;t be confirmed.</span>{' '}
              {addrError}
            </div>
          </div>
        )}

        <div className="pt-2">
          {/* iter125 — Restaurant Agreement consent, mandatory before submit. */}
          <div className="mb-4">
            <AgreementCheckbox
              checked={acceptedAgreement}
              onChange={(v) => {
                setAcceptedAgreement(v);
                if (v) setErrors((er) => ({ ...er, acceptedAgreement: undefined }));
              }}
              agreementLabel="Restaurant Agreement"
              agreementHref="/restaurant-agreement"
              testid="account-restaurant-agreement"
              errorText={errors.acceptedAgreement}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="account-submit"
            className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] tracking-tight disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
          >
            {submitting ? 'Creating account…' : 'Create Account · Continue to Plan'}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <p className="mt-3 text-center text-[12px] text-white/45">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-yellow-300 hover:text-yellow-200 underline-offset-4 hover:underline font-semibold"
            >
              Sign in
            </button>
          </p>
        </div>
      </form>
    </OnboardingShell>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Local UI helpers
// ──────────────────────────────────────────────────────────────────────

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-yellow-500/50 focus:bg-white/[0.05] outline-none text-[14px] text-white placeholder-white/30 transition-all';

function Field({ label, required, optional, Icon, error, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[12px] font-bold text-white/70 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-white/45" />}
        <span>{label}</span>
        {required && <span className="text-rose-300">*</span>}
        {optional && <span className="text-white/35 font-normal">(optional)</span>}
      </label>
      {children}
      {error && <p className="text-[11.5px] text-rose-300 mt-1">{error}</p>}
    </div>
  );
}
